import React from "react";
import { Typography, Box, Chip, Tooltip } from "@mui/material";
import Card from "./UIKit/Card";
import { useNavigate } from "react-router-dom";
import {
  MenuBook as MenuBookIcon,
  AccessTime as AccessTimeIcon,
  Category as CategoryIcon,
  Person as PersonIcon,
  Folder as FolderIcon,
  Bookmark as BookmarkIcon,
  BookmarkBorder as BookmarkBorderIcon,
  VisibilityOff as VisibilityOffIcon,
  Work as WorkIcon,
  Stars as StarsIcon,
  Quiz as QuizIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";

const LessonCard = ({
  lesson,
  showDate = true,
  showType = true,
  showDuration = true,
  variant = "default", // 'default', 'compact', 'detailed', 'fullWidth'
  showCourseInfo = false,
  showModuleInfo = false,
  showAuthor = true,
  customButtons = null,
  sx = {},
  skillsMap = {}, // Справочник навыков для маппинга ID -> название
  professionsMap = {}, // Справочник профессий для маппинга ID -> название
  ...props
}) => {
  const { t } = useTranslation(["profile", "common", "lessons"]);
  const navigate = useNavigate();

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      const day = date.getDate().toString().padStart(2, "0");
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const year = date.getFullYear();
      return `${day}.${month}.${year}`;
    } catch (error) {
      return dateString;
    }
  };



  return (
    <Card
      clickable={false}
      sx={{
        display: "flex",
        flexDirection: "column",
        position: "relative", // For absolute positioning of indicators
        cursor: "pointer", // Add pointer cursor to indicate clickable
        transition: "none", // Remove all transitions
        height: "100%", // Ensure all cards have the same height
        "&:hover": {
          transform: "none", // Remove hover transform
          boxShadow: "none", // Remove hover shadow
        },
        ...sx
      }}
      onClick={() => navigate(`/lessons/${lesson.slug || lesson._id}`)}
      {...props}
    >
      {/* Bookmark + Draft indicator - TOP RIGHT */}
      <Box
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          zIndex: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        {typeof lesson.bookmarked !== 'undefined' && (
          lesson.bookmarked ? (
            <BookmarkIcon fontSize="small" color="primary" />
          ) : (
            <BookmarkBorderIcon fontSize="small" color="action" />
          )
        )}
        {lesson.isDraft && (
          <Tooltip title={t('common:status.draft')}>
            <VisibilityOffIcon
              fontSize="small"
              color="action"
            />
          </Tooltip>
        )}
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', pt:1.5 }}>
        {/* Заголовок уроку */}
        <Box
          display="flex"
          alignItems="flex-start"
          mb={variant === "fullWidth" ? 2 : 1}
        >
              <Box
                sx={{
                  width: { xs: 100, sm: 80, md: 100, lg: 120, xl: 150 },
                  height: { xs: 100, sm: 80, md: 100, lg: 120, xl: 150 },
                  borderRadius: 1,
                  overflow: 'hidden',
                  mr: 1,
                  mt: 0.2,
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {lesson.image ? (
                  <img
                    src={lesson.image}
                    alt={lesson.title}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      objectPosition: 'right center',
                    }}
                  />
                ) : (
                  <MenuBookIcon
                    sx={{
                      color: "grey.400",
                      fontSize: variant === "fullWidth" ? "2rem" : "1.5rem",
                    }}
                  />
                )}
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant={variant === "fullWidth" ? "h6" : "subtitle1"}
                  fontWeight="bold"
                  sx={{
                    lineHeight: 1.3,
                    mb: 0.5,
                  }}
                >
                  {lesson.title}
                </Typography>

                {/* Автор уроку */}
                {showAuthor && lesson.instructor && (
                  <Box
                    display="flex"
                    alignItems="center"
                    sx={{ mb: 0.5 }}
                  >
                    <PersonIcon
                      sx={{
                        mr: 0.5,
                        fontSize: "0.875rem",
                        color: "text.secondary",
                      }}
                    />
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={{
                        fontSize: "0.875rem",
                      }}
                    >
                      {t("common:author")}: {lesson.instructor?.userMeta?.fullName || t("lessons:unknown")}
                    </Typography>
                  </Box>
                )}

                {/* Назва модуля (якщо є) */}
                {showModuleInfo && lesson.module && (
                  <Box
                    display="flex"
                    alignItems="center"
                    sx={{ mb: 0.5 }}
                  >
                    <FolderIcon
                      sx={{
                        mr: 0.5,
                        fontSize: "0.875rem",
                        color: "text.secondary",
                      }}
                    />
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={{
                        fontSize: "0.875rem",
                        fontStyle: "italic",
                      }}
                    >
                      {lesson.module?.title || lesson.module || t("lessons:unknown")}
                    </Typography>
                  </Box>
                )}

                {/* Чіпи з інформацією про урок */}
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: variant === "fullWidth" ? 1.5 : 1,
                    mb: variant === "fullWidth" ? 2 : 1.5,
                  }}
                >
                  {/* Тип уроку */}
                  {showType && lesson.type && (
                    <Chip
                      icon={<CategoryIcon />}
                      label={lesson.type}
                      size={variant === "fullWidth" ? "medium" : "small"}
                      color="primary"
                      variant="outlined"
                    />
                  )}

                  {/* Тип контенту */}
                  {lesson.contentType && lesson.contentType !== "standard" && (
                    <Chip
                      label={lesson.contentType}
                      size={variant === "fullWidth" ? "medium" : "small"}
                      color="secondary"
                      variant="outlined"
                    />
                  )}

                  {/* Тривалість */}
                  {showDuration && lesson.duration && (
                    <Chip
                      icon={<AccessTimeIcon />}
                      label={`${lesson.duration} ${t("common:time.minutes")}`}
                      size={variant === "fullWidth" ? "medium" : "small"}
                      color="info"
                      variant="outlined"
                    />
                  )}

                  {/* Кількість тестів */}
                  {Array.isArray(lesson.tests) && (
                    <Chip
                      icon={<QuizIcon />}
                      label={`${lesson.tests.length} ${t("lessons:lessonEditor.testsCount")}`}
                      size={variant === "fullWidth" ? "medium" : "small"}
                      color={lesson.tests.length > 0 ? "warning" : "default"}
                      variant="outlined"
                    />
                  )}
                </Box>

                {/* Профессии */}
                {lesson.professions && lesson.professions.length > 0 && (
                  <Box
                    sx={{
                      mb: variant === "fullWidth" ? 2 : 1.5,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 600,
                        color: 'text.secondary',
                        display: 'block',
                        mb: 0.5,
                      }}
                    >
                      {t('lessons:lessonEditor.professions')}:
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 0.5,
                      }}
                    >
                      {lesson.professions.slice(0, 3).map((profession, index) => {
                        // Извлекаем название профессии из разных возможных структур
                        let professionName;

                        // Если profession - это число (ID), ищем в справочнике
                        if (typeof profession === 'number') {
                          const professionData = professionsMap[profession];
                          professionName = professionData?.library?.name ||
                                         professionData?.name ||
                                         t('lessons:unknownProfession');
                        } else {
                          // Если это объект, пытаемся извлечь название
                          professionName = profession.term_group?.MainTerm?.value ||
                                         profession.term_group?.name ||
                                         profession.library?.name ||
                                         profession.name ||
                                         profession.title ||
                                         (typeof profession === 'string' ? profession : null) ||
                                         t('lessons:unknownProfession');
                        }

                        return (
                          <Chip
                            key={profession.id || profession._id || index}
                            icon={<WorkIcon />}
                            label={professionName}
                            size="small"
                            color="success"
                            variant="outlined"
                            sx={{
                              fontSize: '0.7rem',
                              height: '20px',
                              '& .MuiChip-label': {
                                px: 0.5,
                              },
                              '& .MuiChip-icon': {
                                fontSize: '0.7rem',
                              },
                            }}
                          />
                        );
                      })}
                      {lesson.professions.length > 3 && (
                        <Chip
                          label={`+${lesson.professions.length - 3}`}
                          size="small"
                          color="default"
                          variant="outlined"
                          sx={{
                            fontSize: '0.7rem',
                            height: '20px',
                            '& .MuiChip-label': {
                              px: 0.5,
                            },
                          }}
                        />
                      )}
                    </Box>
                  </Box>
                )}

                {/* Навыки */}
                {lesson.skills && lesson.skills.length > 0 && (
                  <Box
                    sx={{
                      mb: variant === "fullWidth" ? 2 : 1.5,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 600,
                        color: 'text.secondary',
                        display: 'block',
                        mb: 0.5,
                      }}
                    >
                      {t('lessons:lessonEditor.skills')}:
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 0.5,
                      }}
                    >
                      {lesson.skills.slice(0, 3).map((skill, index) => {
                        // Извлекаем название навыка из разных возможных структур
                        let skillName;

                        // Если skill - это число (ID), ищем в справочнике
                        if (typeof skill === 'number') {
                          const skillData = skillsMap[skill];
                          skillName = skillData?.name || t('lessons:unknownSkill');
                        } else {
                          // Если это объект, пытаемся извлечь название
                          skillName = skill.term_group?.MainTerm?.value ||
                                     skill.term_group?.name ||
                                     skill.name ||
                                     skill.title ||
                                     (typeof skill === 'string' ? skill : null) ||
                                     t('lessons:unknownSkill');
                        }

                        return (
                          <Chip
                            key={skill.id || skill._id || index}
                            icon={<StarsIcon />}
                            label={skillName}
                            size="small"
                            color="info"
                            variant="outlined"
                            sx={{
                              fontSize: '0.7rem',
                              height: '20px',
                              '& .MuiChip-label': {
                                px: 0.5,
                              },
                              '& .MuiChip-icon': {
                                fontSize: '0.7rem',
                              },
                            }}
                          />
                        );
                      })}
                      {lesson.skills.length > 3 && (
                        <Chip
                          label={`+${lesson.skills.length - 3}`}
                          size="small"
                          color="default"
                          variant="outlined"
                          sx={{
                            fontSize: '0.7rem',
                            height: '20px',
                            '& .MuiChip-label': {
                              px: 0.5,
                            },
                          }}
                        />
                      )}
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>


        {/* Опис уроку (якщо є) */}
        {lesson.description && (
          <Typography
            variant={variant === "fullWidth" ? "body1" : "body2"}
            color="textSecondary"
            sx={{
              mb: variant === "fullWidth" ? 3 : 2.5,
              lineHeight: 1.4,
              display: variant === "fullWidth" ? "block" : "-webkit-box",
              WebkitLineClamp: variant === "fullWidth" ? "unset" : 2,
              WebkitBoxOrient:
                variant === "fullWidth" ? "unset" : "vertical",
              overflow: variant === "fullWidth" ? "visible" : "hidden",
            }}
          >
            {lesson.description}
          </Typography>
        )}

        {/* Інформація про курс (якщо є) */}
        {showCourseInfo && lesson.course && (
          <Typography
            variant={variant === "fullWidth" ? "body2" : "caption"}
            color="textSecondary"
            display="block"
            sx={{
              mt: variant === "fullWidth" ? 2 : 1,
              mb: 1,
            }}
          >
            {t("profile:used_in_courses")}:{" "}
            {lesson.course?.title || lesson.course || t("lessons:unknown")}
          </Typography>
        )}

        {/* Spacer to push buttons to bottom */}
        <Box sx={{ flex: 1 }} />

        {/* Кнопки */}
        {customButtons && (
          <Box
            sx={{
              mt: variant === "fullWidth" ? 2 : 1.5,
              display: "flex",
              justifyContent: "flex-end",
            }}
            onClick={(e) => e.stopPropagation()} // Prevent card navigation when clicking buttons
          >
            {customButtons}
          </Box>
        )}

        {/* Дата завершення */}
        {showDate && lesson.createdAt && (
          <Typography
            variant="caption"
            color="textSecondary"
            display="block"
            sx={{
              mt: customButtons ? (variant === "fullWidth" ? 1.5 : 1) : "auto",
              pt: variant === "fullWidth" ? 1.5 : 1,
              borderTop: "1px solid",
              borderColor: "divider",
              textAlign: "right",
              fontSize: "0.75rem",
            }}
          >
            {formatDate(lesson.createdAt)}
          </Typography>
        )}
      </Box>
    </Card>
  );
};

export default LessonCard;
